import { EventEmitter } from 'events';
import { RemoteParticipant, ParticipantEvent } from 'livekit-client';

/**
 * Handles data communication from a remote participant
 * Automatically waits for participant readiness and emits decoded data events
 */
class ParticipantDataHandler extends EventEmitter {
    /**
     * 
     * @param {RemoteParticipant} participant 
     */
    constructor(participant) {
        super();
        this.participant = participant;
        this.metadata = null;
        this.isReady = false;
        this.dataListener = null;
        this.metadataListener = null;

        this.initialize();
    }

    /**
     * Initialize the handler - wait for participant and set up listeners
     */
    async initialize() {
        try {
            // Wait for participant to be active
            await this.participant.waitUntilActive();
            console.log("Participant is now active:", this.participant.identity);

            // Parse and store metadata
            this.updateMetadata();
            this.isReady = true;

            // Listen for metadata changes
            this.metadataListener = () => this.updateMetadata();
            this.participant.on(ParticipantEvent.ParticipantMetadataChanged, this.metadataListener);

            // Listen for data updates
            this.dataListener = (payload, kind) => {
                this.handleDataReceived(payload, kind);
            };
            this.participant.on(ParticipantEvent.DataReceived, this.dataListener);

            // Emit ready event
            this.emit('ready', { meta: this.metadata });

        } catch (error) {
            console.error('Error initializing ParticipantDataHandler:', error);
            this.emit('error', error);
        }
    }

    /**
     * Update metadata from participant info
     */
    updateMetadata() {
        try {
            this.metadata = JSON.parse(this.participant.participantInfo?.metadata || "{}");
            console.log("Participant metadata loaded/updated:", this.metadata);
        } catch (error) {
            console.error("Failed to parse participant meta", error);
            this.metadata = {};
        }
    }

    /**
     * Handle incoming data and emit topic-specific events
     * @param {Uint8Array} payload - The data payload
     * @param {string} kind - reliable or unreliable
     */
    handleDataReceived(payload, kind) {
        let eventName = 'data';
        try {
            // Decode Uint8Array to JSON
            const decoder = new TextDecoder();
            const jsonString = decoder.decode(payload);
            const data = JSON.parse(jsonString);

            eventName = data.topic || 'data';

            // Emit event with topic name - always emit regardless of manager subscriptions
            this.emit(eventName, {
                payload: data,
                meta: this.metadata,
                kind: kind
            });

        } catch (error) {
            console.error('Error decoding data from participant:', error);
            this.emit('error', { error, eventName });
        }
    }

    /**
     * Get current metadata
     * @returns {Object} Current participant metadata
     */
    getMetadata() {
        return this.metadata;
    }

    /**
     * Check if handler is ready
     * @returns {boolean}
     */
    ready() {
        return this.isReady;
    }

    /**
     * Clean up listeners
     */
    destroy() {
        if (this.dataListener) {
            this.participant.off(ParticipantEvent.DataReceived, this.dataListener);
        }
        if (this.metadataListener) {
            this.participant.off(ParticipantEvent.ParticipantMetadataChanged, this.metadataListener);
        }
        this.removeAllListeners();
        console.log("ParticipantDataHandler destroyed for:", this.participant.identity);
    }
}

/**
 * Manages multiple ParticipantDataHandler instances
 * Automatically handles participant lifecycle
 */
class ParticipantDataManager extends EventEmitter {
    constructor(participants = null) {
        super();
        this.handlers = new Map(); // Map of participant.identity -> ParticipantDataHandler
        this.topicSubscriptions = new Map(); // Map of topic -> {managerCallback, handlerListeners}
        if (participants) this.onParticipantsUpdated(participants);
    }

    /**
     * Update handlers based on current participants
     * Removes obsolete handlers and adds new ones
     * @param {Map<string, RemoteParticipant>} currentParticipants - Current remote participants
     */
    onParticipantsUpdated(currentParticipants) {
        const currentIdentities = new Set(currentParticipants.keys());
        const existingIdentities = new Set(this.handlers.keys());

        // Remove obsolete handlers
        existingIdentities.forEach(identity => {
            if (!currentIdentities.has(identity)) {
                console.log('Removing handler for disconnected participant:', identity);
                
                // Clean up topic subscriptions for this participant
                this._unsubscribeAllTopicsForParticipant(identity);
                
                const handler = this.handlers.get(identity);
                handler.destroy();
                this.handlers.delete(identity);
                this.emit('participantRemoved', identity);
            }
        });

        // Add new handlers
        currentIdentities.forEach(identity => {
            if (!existingIdentities.has(identity)) {
                const participant = currentParticipants.get(identity);
                console.log('Adding handler for new participant:', identity);
                this.addParticipant(participant);
            }
        });
    }

    /**
     * Add a participant handler
     * @param {RemoteParticipant} participant
     */
    addParticipant(participant) {
        if (this.handlers.has(participant.identity)) {
            console.warn('Handler already exists for participant:', participant.identity);
            return;
        }

        const handler = new ParticipantDataHandler(participant);
        this.handlers.set(participant.identity, handler);

        // Forward ready and error events immediately
        const readyListener = (data) => {
            this.emit('ready', {
                participantId: participant.identity,
                ...data
            });
        };
        handler.on('ready', readyListener);

        const errorListener = (data) => {
            this.emit('error', {
                participantId: participant.identity,
                ...data
            });
        };
        handler.on('error', errorListener);

        // Now check if there are any active topic subscriptions and subscribe to them
        this.topicSubscriptions.forEach((subscription, topic) => {
            this._subscribeParticipantToTopic(participant.identity, handler, topic);
        });

        this.emit('participantAdded', {
            participantId: participant.identity,
            handler
        });

        return handler;
    }

    /**
     * Subscribe a participant to a specific topic
     * @param {string} participantIdentity - The participant identity
     * @param {ParticipantDataHandler} handler - The handler instance
     * @param {string} topic - The topic to subscribe to
     */
    _subscribeParticipantToTopic(participantIdentity, handler, topic) {
        // Create a listener that forwards the event with participant ID
        const handlerListener = (data) => {
            this.emit(topic, {
                participantId: participantIdentity,
                ...data
            });
        };

        // Attach the listener to the handler
        handler.on(topic, handlerListener);

        // Store this listener in the subscription for cleanup
        if (!this.topicSubscriptions.get(topic).handlerListeners) {
            this.topicSubscriptions.get(topic).handlerListeners = new Map();
        }
        this.topicSubscriptions.get(topic).handlerListeners.set(participantIdentity, {
            source: handler,
            listener: handlerListener
        });
    }

    /**
     * Unsubscribe a participant from all topics
     * @param {string} participantIdentity - The participant identity
     */
    _unsubscribeAllTopicsForParticipant(participantIdentity) {
        this.topicSubscriptions.forEach((subscription, topic) => {
            if (subscription.handlerListeners && subscription.handlerListeners.has(participantIdentity)) {
                const { source, listener } = subscription.handlerListeners.get(participantIdentity);
                source.off(topic, listener);
                subscription.handlerListeners.delete(participantIdentity);
            }
        });
    }

    /**
     * Remove a participant handler
     * @param {string} participantId - Participant identity
     */
    removeParticipant(participantId) {
        const handler = this.handlers.get(participantId);
        if (handler) {
            this._unsubscribeAllTopicsForParticipant(participantId);
            
            handler.destroy();
            this.handlers.delete(participantId);
            this.emit('participantRemoved', participantId);
        }
    }

    /**
     * Get a specific participant handler
     * @param {string} participantId
     * @returns {ParticipantDataHandler|null}
     */
    getHandler(participantId) {
        return this.handlers.get(participantId) || null;
    }

    /**
     * Get all handlers
     * @returns {Map<string, ParticipantDataHandler>}
     */
    getAllHandlers() {
        return this.handlers;
    }

    /**
     * Subscribe to a specific topic across all participants
     * @param {string} topic - The topic name
     * @param {Function} callback - Callback function (participantId, payload, meta, kind) => void
     */
    onTopic(topic, callback) {
        // Check if we already have a subscription for this topic
        if (this.topicSubscriptions.has(topic)) {
            console.warn(`Already subscribed to topic: ${topic}`);
            return () => {}; // Return empty cleanup function
        }

        // Create the manager listener
        const managerListener = ({ participantId, payload, meta, kind }) => {
            callback(participantId, payload, meta, kind);
        };

        // Register the listener on the manager
        this.on(topic, managerListener);

        // Create the subscription record
        const subscription = {
            managerCallback: callback,
            managerListener: managerListener,
            handlerListeners: new Map() // Will store {source, listener} for each participant
        };
        this.topicSubscriptions.set(topic, subscription);

        // Subscribe all existing participants to this topic
        this.handlers.forEach((handler, identity) => {
            this._subscribeParticipantToTopic(identity, handler, topic);
        });

        return () => {
            // Cleanup function
            if (this.topicSubscriptions.has(topic)) {
                const subscription = this.topicSubscriptions.get(topic);
                
                // Remove the manager listener
                this.off(topic, subscription.managerListener);
                
                // Remove all handler listeners for this topic
                subscription.handlerListeners.forEach(({ source, listener }) => {
                    source.off(topic, listener);
                });
                
                // Delete the subscription
                this.topicSubscriptions.delete(topic);
            }
        };
    }

    /**
     * Clean up all handlers
     */
    destroy() {
        // Clean up all topic subscriptions
        this.topicSubscriptions.forEach((subscription, topic) => {
            // Remove the manager listener
            this.off(topic, subscription.managerListener);
            
            // Remove all handler listeners
            subscription.handlerListeners.forEach(({ source, listener }) => {
                source.off(topic, listener);
            });
        });
        this.topicSubscriptions.clear();

        this.handlers.forEach(handler => handler.destroy());
        this.handlers.clear();
        this.removeAllListeners();
        console.log('ParticipantDataManager destroyed');
    }
}

export { ParticipantDataHandler, ParticipantDataManager };
export default ParticipantDataHandler;